import { ReactNode, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
    Activity,
    Archive,
    ArrowLeft,
    Calendar,
    Coins,
    Eraser,
    Hash,
    Info,
    LogOut,
    MapPin,
    StickyNote,
    User
} from "lucide-react";
import { useAuth } from "@/context/use-auth";
import { useCommonTranslation, useMembersTranslation } from "@/i18n/hooks";
import { Heading } from "@/components/tailwind/heading";
import { Text } from "@/components/tailwind/text";
import { Card } from "@/components/ui/card";
import { formatDateString, formatDateTimeString } from "@/lib/utils";
import { MemberAuditLogSection } from "@/pages/members/details/member-audit-log-section";
import {
    getMemberRetainedData,
    MemberRetainedData,
    MemberRetainedDataTargetMember
} from "@/services/member-retained-data-services";
import log from '@/lib/logger'

const FORMER_MEMBERS_ROUTE = '/reports/former-members';

function SectionCard({ titleKey, children }: { titleKey: string; children: ReactNode }) {
    const { t } = useMembersTranslation();

    return (
        <Card>
            <div className="p-6">
                <div className="mb-6 text-2xl font-semibold leading-none tracking-tight" role="heading">
                    {t(titleKey)}
                </div>
                <div className="space-y-6">{children}</div>
            </div>
        </Card>
    );
}

function ValueRow({ icon, label, value }: { icon: ReactNode; label: string; value: ReactNode }) {
    const { t: tCommon } = useCommonTranslation();

    return (
        <div className="flex items-center gap-3">
            {icon}
            <Text>
                {label}:&nbsp;
                {value ?? <span className="italic">{tCommon("unknown-detail")}</span>}
            </Text>
        </div>
    );
}

function TargetMemberLink({ target }: { target: MemberRetainedDataTargetMember }) {
    const targetLabel = `#${target.memberNo} ${target.firstName} ${target.lastName}`;
    // A resigned target has no detail page; its retained-data view takes over.
    const targetRoute = target.resigned
        ? `/members/${target.id}/retained-data`
        : `/members/${target.id}`;

    return (
        <Link
            to={targetRoute}
            data-testid="retained-data-target-member-link"
            className="text-blue-600 hover:underline dark:text-blue-400"
        >
            {targetLabel}
        </Link>
    );
}

export default function MemberRetainedDataPage() {
    const { memberId } = useParams();
    const { token } = useAuth();
    const { t } = useMembersTranslation();
    const [data, setData] = useState<MemberRetainedData | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        if (!token || !memberId) return;
        let ignore = false;
        setIsLoading(true);
        setHasError(false);
        getMemberRetainedData(token, memberId)
            .then((result) => { if (!ignore) setData(result); })
            .catch((error) => {
                if (ignore) return;
                log.error("Error loading member retained data:", error);
                setHasError(true);
            })
            .finally(() => { if (!ignore) setIsLoading(false); });
        return () => { ignore = true; };
    }, [token, memberId]);

    const iconClass = "h-4 w-4 shrink-0 text-gray-500 dark:text-gray-400";

    return (
        <div className="container mx-auto py-6 max-w-5xl space-y-6">
            <Link
                to={FORMER_MEMBERS_ROUTE}
                data-testid="retained-data-back-link"
                className="inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900 hover:underline dark:text-zinc-400 dark:hover:text-zinc-100"
            >
                <ArrowLeft className="h-4 w-4"/>
                {t('memberRetainedData.backToList')}
            </Link>
            <div className="flex items-center gap-3">
                <Archive className="h-8 w-8 text-blue-500" />
                <div>
                    <Heading>{t('memberRetainedData.title')}</Heading>
                    <Text className="text-sm text-zinc-500 dark:text-zinc-400">
                        {t('memberRetainedData.subtitle')}
                    </Text>
                </div>
            </div>

            {isLoading ? (
                <Card className="p-6">
                    <Text className="text-zinc-500">{t('memberRetainedData.loading')}</Text>
                </Card>
            ) : hasError || !data ? (
                <Card className="p-6">
                    <Text className="text-red-500">{t('memberRetainedData.error')}</Text>
                </Card>
            ) : (
                <>
                    <div
                        className="flex items-start gap-3 rounded border-l-4 border-blue-400 dark:border-blue-500 bg-blue-50 dark:bg-blue-950 p-4"
                        data-testid="retained-data-notice"
                    >
                        <Info className="h-5 w-5 shrink-0 text-blue-500 dark:text-blue-400"/>
                        <Text className="text-blue-800 dark:text-blue-200">
                            {t('memberRetainedData.retainedNotice')}
                        </Text>
                    </div>

                    <SectionCard titleKey="memberRetainedData.sections.baseData.title">
                        <ValueRow
                            icon={<Hash className={iconClass}/>}
                            label={t('memberRetainedData.sections.baseData.memberNo')}
                            value={data.member.memberNo}
                        />
                        <ValueRow
                            icon={<User className={iconClass}/>}
                            label={t('memberRetainedData.sections.baseData.name')}
                            value={`${data.member.firstName} ${data.member.lastName}`}
                        />
                        <div className="flex items-center gap-3">
                            <Calendar className={iconClass}/>
                            <Text>
                                {t('memberRetainedData.sections.baseData.membershipPeriod', {
                                    start: formatDateString(data.member.membershipStart),
                                    end: formatDateString(data.member.membershipEnd)
                                })}
                            </Text>
                        </div>
                        <ValueRow
                            icon={<Activity className={iconClass}/>}
                            label={t('memberRetainedData.sections.baseData.status')}
                            value={t(`memberRetainedData.status.${data.member.memberStatus}`)}
                        />
                        <ValueRow
                            icon={<StickyNote className={iconClass}/>}
                            label={t('memberRetainedData.sections.baseData.currentNotes')}
                            value={data.member.currentNotes || (
                                <span className="italic">
                                    {t('memberRetainedData.sections.baseData.noNotes')}
                                </span>
                            )}
                        />
                        <div className="flex items-center gap-3">
                            <Coins className={iconClass}/>
                            <Link
                                to={`/members/${memberId}/points-statement?from=former-members`}
                                data-testid="retained-data-points-statement-link"
                                className="text-blue-600 hover:underline dark:text-blue-400"
                            >
                                {t('memberRetainedData.pointsStatementLink')}
                            </Link>
                        </div>
                    </SectionCard>

                    <SectionCard titleKey="memberRetainedData.sections.resignation.title">
                        <ValueRow
                            icon={<LogOut className={iconClass}/>}
                            label={t('memberRetainedData.sections.resignation.reason')}
                            value={data.resignation.reasonName}
                        />
                        <ValueRow
                            icon={<Coins className={iconClass}/>}
                            label={t('memberRetainedData.sections.resignation.pointsTarget')}
                            value={data.resignation.pointsTarget && t(
                                `memberRetainedData.sections.resignation.pointsTargetValue.${data.resignation.pointsTarget}`
                            )}
                        />
                        {data.resignation.pointsTargetMember && (
                            <ValueRow
                                icon={<User className={iconClass}/>}
                                label={t('memberRetainedData.sections.resignation.targetMember')}
                                value={<TargetMemberLink target={data.resignation.pointsTargetMember}/>}
                            />
                        )}
                        <ValueRow
                            icon={<Calendar className={iconClass}/>}
                            label={t('memberRetainedData.sections.resignation.settledAt')}
                            value={data.resignation.pointsSettledAt
                                && formatDateTimeString(data.resignation.pointsSettledAt)}
                        />
                        <ValueRow
                            icon={<User className={iconClass}/>}
                            label={t('memberRetainedData.sections.resignation.recordedBy')}
                            value={data.resignation.recordedByName}
                        />
                    </SectionCard>

                    <SectionCard titleKey="memberRetainedData.sections.address.title">
                        <div className="flex items-center gap-3" data-testid="retained-data-address">
                            <MapPin className={iconClass}/>
                            {data.address ? (
                                <Text>
                                    {[data.address.street, data.address.houseNumber].filter(Boolean).join(' ')},{' '}
                                    {data.address.postcode} {data.address.city}
                                </Text>
                            ) : (
                                <Text className="italic">
                                    {t('memberRetainedData.sections.address.none')}
                                </Text>
                            )}
                        </div>
                    </SectionCard>

                    <SectionCard titleKey="memberRetainedData.sections.cleanup.title">
                        <div className="flex items-center gap-3">
                            <Eraser className={iconClass}/>
                            {data.cleanup ? (
                                <Text data-testid="retained-data-cleaned-at">
                                    {t('memberRetainedData.sections.cleanup.cleanedAt', {
                                        date: formatDateTimeString(data.cleanup.cleanedAt)
                                    })}
                                </Text>
                            ) : (
                                <Text className="italic" data-testid="retained-data-cleanup-pending">
                                    {t('memberRetainedData.sections.cleanup.pending')}
                                </Text>
                            )}
                        </div>
                        <div className="rounded-lg border border-zinc-200 dark:border-zinc-700 p-4">
                            <Text className="text-sm text-zinc-500 dark:text-zinc-400">
                                {t('memberRetainedData.sections.cleanup.deletedCategoriesIntro')}{' '}
                                {t('memberRetainedData.sections.cleanup.deletedCategories')}
                            </Text>
                        </div>
                    </SectionCard>

                    {memberId && <MemberAuditLogSection memberId={memberId}/>}
                </>
            )}
        </div>
    );
}
